import React from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';
import { 
  PlusIcon, 
  FileTextIcon, 
  ClockIcon, 
  PlayIcon, 
  PauseIcon, 
  CheckCircleIcon, 
  EyeIcon, 
  DownloadIcon,
  GraduationCapIcon,
  ArrowRightIcon,
  BookOpenIcon,
  MessagesSquareIcon,
  BotIcon
} from 'lucide-react';

const MY_PLANS = gql`
  query MySuccessionPlans {
    getCurrentUser {
      id
      company {
        id
        features
      }
    }
    mySuccessionPlans {
      id
      status
      completionPercentage
      updatedAt
      createdAt
      completedAt
      ceCreditEarned
      userPlanResponses {
        id
      }
      planTemplate {
        id
        slug
        name
        icon
        planType
        ceEligible
        ceCreditHours
        planModules {
          id
          planSections {
            id
            planQuestions {
              id
            }
          }
        }
      }
    }
  }
`;

const StatusBadge = ({ status }) => {
  const intl = useIntl();
  const config = {
    in_progress: {
      label: intl.formatMessage({ id: 'succession.plans.status_in_progress' }),
      classes: "bg-blue-50 text-blue-700 border-blue-200",
      icon: PlayIcon
    },
    paused: {
      label: intl.formatMessage({ id: 'succession.plans.status_paused' }),
      classes: "bg-gray-100 text-gray-700 border-gray-200",
      icon: PauseIcon
    },
    completed: {
      label: intl.formatMessage({ id: 'succession.plans.status_completed' }),
      classes: "bg-green-50 text-green-700 border-green-200",
      icon: CheckCircleIcon
    },
    not_started: {
      label: intl.formatMessage({ id: 'succession.plans.status_not_started' }),
      classes: "bg-gray-50 text-gray-600 border-gray-200",
      icon: FileTextIcon
    }
  };

  const { label, classes, icon: Icon } = config[status] || config.in_progress;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${classes}`}>
      <Icon className="w-3 h-3 mr-1.5" />
      {label}
    </span>
  );
};

const ProgressBar = ({ value }) => (
  <div className="h-2 w-full bg-succession-bg-secondary rounded-full overflow-hidden">
    <div 
      className="h-full bg-succession-primary transition-all duration-500 ease-in-out" 
      style={{ width: `${value}%` }}
    />
  </div>
);

const CeCreditBadge = ({ ceCredit, planSlug }) => {
  const intl = useIntl();
  if (!ceCredit?.available) return null;

  const planUrl = `/succession/plans/${planSlug}`;

  if (ceCredit.earned) {
    // Earned state - static display
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20">
        <GraduationCapIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
        <span className="text-xs font-medium text-green-700 dark:text-green-300">
          {intl.formatMessage({ id: 'succession.plans.ce_credit_earned' }, { hours: ceCredit.creditHours })}
        </span>
        <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
      </div>
    );
  }

  // Available state - clickable CTA
  return (
    <a
      href={`${planUrl}?tab=ce-assessment`}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-succession-primary/10 to-succession-secondary/10 border border-succession-primary/30 hover:border-succession-primary/50 hover:shadow-sm transition-all group"
    >
      <GraduationCapIcon className="w-4 h-4 text-succession-primary" />
      <span className="text-xs font-semibold text-succession-primary">
        {intl.formatMessage({ id: 'succession.plans.ce_credit_earn' }, { hours: ceCredit.creditHours })}
      </span>
      <ArrowRightIcon className="w-3.5 h-3.5 text-succession-primary group-hover:translate-x-0.5 transition-transform" />
    </a>
  );
};

const PlanCard = ({ plan, ceCredit }) => {
  const intl = useIntl();
  // Calculate counts
  const answeredCount = plan.userPlanResponses?.length || 0;
  
  // Flatten structure to count total questions
  const totalQuestions = plan.planTemplate.planModules?.reduce((modAcc, mod) => {
    return modAcc + (mod.planSections?.reduce((secAcc, sec) => {
      return secAcc + (sec.planQuestions?.length || 0);
    }, 0) || 0);
  }, 0) || 0;

  const lastActivity = plan.updatedAt
    ? new Date(plan.updatedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : intl.formatMessage({ id: 'succession.plans.date_unknown' });

  const createdDate = plan.createdAt
    ? new Date(plan.createdAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  const completedDate = plan.completedAt
    ? new Date(plan.completedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <Card className="p-6 hover:shadow-succession-lg transition-shadow duration-200">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
        {/* Left: Plan Info */}
        <div className="flex-1 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-succession-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileTextIcon className="w-6 h-6 text-succession-primary" />
            </div>
            <div className="flex-1 min-w-0">
              {/* Title Row - Using inline styles for precise alignment */}
              <div 
                style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  alignItems: 'center', 
                  gap: '12px', 
                  marginBottom: '8px' 
                }}
              >
                <span 
                  style={{ 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    lineHeight: 1, 
                    margin: 0, 
                    padding: 0 
                  }}
                  className="text-succession-text-primary"
                >
                  {plan.planTemplate.name}
                </span>
                <StatusBadge status={plan.status} />
              </div>
              <p className="text-sm text-succession-text-secondary mb-3 capitalize" style={{ margin: 0, marginBottom: '12px' }}>
                {plan.planTemplate.planType || 'Succession Planning'}
              </p>

              {/* Progress */}
              <div className="space-y-2 w-full">
                <div className="flex items-center justify-between text-xs text-succession-text-secondary">
                  <span>
                    {intl.formatMessage({ id: 'succession.plans.questions_answered' }, { answered: answeredCount, total: totalQuestions })}
                  </span>
                  <span className="font-medium text-succession-text-primary">
                    {plan.completionPercentage}%
                  </span>
                </div>
                <ProgressBar value={plan.completionPercentage} />
              </div>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-succession-text-muted">
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-3 h-3" />
                  {intl.formatMessage({ id: 'succession.plans.last_activity' }, { date: lastActivity })}
                </div>
                {createdDate && <div>{intl.formatMessage({ id: 'succession.plans.created_date' }, { date: createdDate })}</div>}
                {completedDate && <div>{intl.formatMessage({ id: 'succession.plans.completed_date' }, { date: completedDate })}</div>}
              </div>
              
              {/* CE Credit Badge - shown for completed plans with CE available */}
              {plan.status === "completed" && ceCredit?.available && (
                <div className="mt-4">
                  <CeCreditBadge ceCredit={ceCredit} planSlug={plan.planTemplate.slug} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex flex-col sm:flex-row md:flex-col gap-2 w-full md:w-auto pt-4 md:pt-0 border-t md:border-t-0 border-succession-border">
          {plan.status === "in_progress" || plan.status === "paused" || plan.status === "not_started" ? (
            <Button
              href={`/succession/plans/${plan.planTemplate.slug}`}
              as="a"
              className="w-full md:w-32 justify-center"
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              {plan.status === "paused" ? intl.formatMessage({ id: 'succession.plans.button_resume' }) : intl.formatMessage({ id: 'succession.plans.button_continue' })}
            </Button>
          ) : null}

          {plan.status === "completed" && (
            <>
              <Button
                href={`/succession/plans/${plan.planTemplate.slug}`}
                as="a"
                className="w-full md:w-32 justify-center"
              >
                <EyeIcon className="w-4 h-4 mr-2" />
                {intl.formatMessage({ id: 'succession.plans.button_view_plan' })}
              </Button>
              <Button
                href={`/succession/plans/${plan.planTemplate.slug}?action=export`}
                as="a"
                variant="outline"
                className="w-full md:w-32 justify-center"
              >
                <DownloadIcon className="w-4 h-4 mr-2" />
                {intl.formatMessage({ id: 'succession.plans.button_export_pdf' })}
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const SuccessionPlansIndex = () => {
  const intl = useIntl();
  const { loading, error, data } = useQuery(MY_PLANS);

  if (loading) {
    return (
      <SingleColumnTemplate>
        <div className="p-12 text-center text-succession-text-muted">
          {intl.formatMessage({ id: 'succession.plans.loading_plans' })}
        </div>
      </SingleColumnTemplate>
    );
  }

  if (error) {
    return (
      <SingleColumnTemplate>
        <div className="p-12 text-center text-red-500">
          {intl.formatMessage({ id: 'succession.plans.error_loading_plans' }, { message: error.message })}
        </div>
      </SingleColumnTemplate>
    );
  }

  const plans = data?.mySuccessionPlans || [];
  const currentUser = data?.getCurrentUser;
  
  // Check if plan_builder_ce feature is enabled
  const features = currentUser?.company?.features || [];
  const ceFeatureEnabled = features.includes('plan_builder_ce');
  
  // Helper to build CE credit info for a plan
  const getCeCredit = (plan) => {
    if (!ceFeatureEnabled || !plan.planTemplate?.ceEligible) {
      return null;
    }
    return {
      available: true,
      earned: plan.ceCreditEarned || false,
      creditHours: plan.planTemplate.ceCreditHours || 1,
    };
  };

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'succession.plans.index_title' })}
      subtitle={intl.formatMessage({ id: 'succession.plans.index_subtitle' })}
      actions={plans.length > 0 ? (
        <Button href="/succession/plans/new" as="a" variant="primary">
          <PlusIcon className="w-4 h-4 mr-2" />
          {intl.formatMessage({ id: 'succession.plans.create_new_plan' })}
        </Button>
      ) : null}
    >
      {plans.length === 0 ? (
        <Card className="p-8 md:p-10 max-w-2xl mx-auto border border-succession-border bg-succession-bg-secondary rounded-2xl">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 rounded-xl bg-succession-primary/10 border border-succession-primary/20 flex items-center justify-center mb-5">
              <FileTextIcon className="w-7 h-7 text-succession-primary" />
            </div>

            <div className="w-full max-w-2xl border-t border-succession-border pt-5">
              <h3 className="text-3xl font-semibold text-succession-text-primary mb-3">
                {intl.formatMessage({ id: 'succession.plans.empty_state_title' })}
              </h3>
              <p className="text-succession-text-secondary max-w-2xl mx-auto mb-6">
                {intl.formatMessage({ id: 'succession.plans.empty_state_description' })}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left mb-6">
                <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3">
                  <BookOpenIcon className="w-4 h-4 text-succession-primary mb-1.5" />
                  <p className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.feature_learning_title' })}</p>
                  <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.feature_learning_description' })}</p>
                </div>
                <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3">
                  <MessagesSquareIcon className="w-4 h-4 text-succession-primary mb-1.5" />
                  <p className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.feature_guided_title' })}</p>
                  <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.feature_guided_description' })}</p>
                </div>
                <div className="rounded-lg border border-succession-border bg-succession-bg-secondary p-3">
                  <BotIcon className="w-4 h-4 text-succession-primary mb-1.5" />
                  <p className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.feature_ai_title' })}</p>
                  <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.feature_ai_description' })}</p>
                </div>
              </div>
            </div>

            <Button href="/succession/plans/new" as="a" variant="primary" size="lg">
              <PlusIcon className="w-4 h-4 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.create_first_plan' })}
            </Button>

            <p className="text-xs text-succession-text-muted mt-3">
              {intl.formatMessage({ id: 'succession.plans.empty_state_footer' })}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} ceCredit={getCeCredit(plan)} />
          ))}
        </div>
      )}
    </SingleColumnTemplate>
  );
};

export default SuccessionPlansIndex;
